import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { IconChevronDown } from '@repo/svgs';
import { Popover } from '@/components/ui/Popover';
import { DropdownField } from '@/components/ui/DropdownField';
import { Input } from '@/components/ui/Input';

type DropdownVariant = 'default' | 'filled';

export type DropdownOption<T = string> = {
  value: T;
  label: React.ReactNode;
};

export interface DropdownProps<T = string> {
  // Value + change
  value?: T | null;
  onChange?: (value: T | null) => void;

  // Data
  options?: Array<DropdownOption<T>>;
  renderOption?: (option: DropdownOption<T>, selected: boolean) => React.ReactNode;

  // Presentation
  label?: string;
  placeholder?: string;
  variant?: DropdownVariant;
  disabled?: boolean;
  alert?: boolean;
  helperText?: React.ReactNode;
  helperIcon?: React.ReactNode;
  className?: string;
  wrapperClassName?: string;

  // Menu behavior/sizing
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  menuClassName?: string;
  matchTriggerWidth?: boolean; // default false (size to content)
  animated?: boolean;
  animationDurationMs?: number;

  /**
   * When true, the trigger becomes a search input: it shows the selected label when idle and
   * accepts typing to filter the options (the menu updates live). Mirrors the mobile dropdown.
   */
  searchable?: boolean;
  /** Message shown when a search yields no matches. */
  noResultsText?: string;
}

export function Dropdown<T = string>({
  value,
  onChange,
  options,
  renderOption,
  label,
  placeholder = 'Select…',
  variant = 'default',
  disabled,
  alert,
  helperText,
  helperIcon,
  className,
  wrapperClassName,
  open,
  defaultOpen,
  onOpenChange,
  menuClassName,
  matchTriggerWidth = false,
  searchable = false,
  noResultsText = 'No results found',
}: DropdownProps<T>) {
  const isControlledOpen = typeof open === 'boolean';
  const [internalOpen, setInternalOpen] = useState<boolean>(!!defaultOpen);
  const isOpen = isControlledOpen ? open : internalOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlledOpen) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlledOpen, onOpenChange],
  );

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const id = useId();
  const listboxId = `${id}-listbox`;

  // Search state (only used when `searchable`).
  const [query, setQuery] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  // Trigger styling delegated to DropdownField
  // With `exactOptionalPropertyTypes`, avoid passing `undefined` for optional props.
  const showAlert = Boolean(alert) && !disabled;

  // Resolve label from value when options provided
  const selectedLabel = useMemo(() => {
    if (!options) return null;
    const found = options.find(o => Object.is(o.value, value));
    return found?.label ?? null;
  }, [options, value]);

  // String form of the selected label, used for the search input value + filtering.
  // `searchable` dropdowns are expected to use plain string labels.
  const selectedText = typeof selectedLabel === 'string' ? selectedLabel : '';

  // Options shown in the menu. When searching, filter by the typed query (but show all
  // while the query still matches the current selection, like the mobile dropdown).
  const displayedOptions = useMemo(() => {
    const all = options ?? [];
    if (!searchable) return all;
    const q = query.trim().toLowerCase();
    if (!q || query === selectedText) return all;
    return all.filter(o => String(o.label).toLowerCase().includes(q));
  }, [options, searchable, query, selectedText]);

  // Outside-click and positioning handled by Popover

  // Keyboard interactions
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  useEffect(() => {
    if (!isOpen) setActiveIndex(-1);
  }, [isOpen]);

  const onTriggerKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (disabled) return;
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setOpen(true);
        setActiveIndex(0);
      }
    },
    [disabled, setOpen],
  );

  const onListKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!displayedOptions.length) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex(prev => (prev + 1) % displayedOptions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex(prev => (prev - 1 + displayedOptions.length) % displayedOptions.length);
        return;
      }
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        const idx = activeIndex >= 0 ? activeIndex : 0;
        const opt = displayedOptions[idx];
        if (opt) {
          onChange?.(opt.value);
          setOpen(false);
          triggerRef.current?.focus();
        }
      }
    },
    [activeIndex, onChange, displayedOptions, setOpen],
  );

  // Render option helper
  const renderOptionNode = useCallback(
    (opt: DropdownOption<T>, selected: boolean) => {
      if (renderOption) return renderOption(opt, selected);
      return (
        <div
          key={String((opt.value as unknown) ?? '')}
          role="option"
          aria-selected={selected}
          className={cn(
            // layout
            'flex items-center',
            // size / gaps
            'w-full rounded-xl px-2 py-3 gap-4',
            // text / styles
            'text-body-2 text-primary',
            selected ? 'bg-active' : 'hover:bg-secondary-hover',
          )}
        >
          {opt.label}
        </div>
      );
    },
    [renderOption],
  );

  // Click select handler
  const onSelect = useCallback(
    (opt: DropdownOption<T>) => {
      onChange?.(opt.value);
      setOpen(false);
      if (searchable) {
        setQuery('');
        setIsEditing(false);
        inputRef.current?.blur();
      } else {
        triggerRef.current?.focus();
      }
    },
    [onChange, setOpen, searchable],
  );

  // Menu node (rendered inside Popover)
  const menu = (
    <div
      ref={menuRef}
      id={listboxId}
      role="listbox"
      aria-orientation="vertical"
      className={cn(
        // layout
        'flex flex-col',
        // size / gaps
        'gap-1 p-3 rounded-3xl max-h-72 overflow-auto',
        // text / styles
        'bg-primary border border-tertiary shadow-lg',
        menuClassName,
      )}
      onKeyDown={onListKeyDown}
    >
      {searchable && displayedOptions.length === 0 ? (
        <div className="px-2 py-3 text-body-2 text-secondary">{noResultsText}</div>
      ) : (
        displayedOptions.map((opt, idx) => {
          const selected = Object.is(opt.value, value);
          const node = renderOptionNode(opt, selected);
          return (
            <button
              key={String((opt.value as unknown) ?? idx)}
              type="button"
              role="option"
              aria-selected={selected}
              className={cn('text-left')}
              // Keep the search input focused when clicking an option.
              onMouseDown={e => e.preventDefault()}
              onMouseEnter={() => setActiveIndex(idx)}
              onClick={() => onSelect(opt)}
            >
              {node}
            </button>
          );
        })
      )}
    </div>
  );

  // Root render
  return (
    <div className={cn('flex w-full flex-col gap-1', wrapperClassName)}>
      <div ref={anchorRef}>
        {searchable ? (
          <Input
            ref={inputRef}
            label={label || ''}
            placeholder={placeholder}
            value={isEditing ? query : selectedText}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-controls={isOpen ? listboxId : undefined}
            {...(showAlert ? { alert: true } : {})}
            {...(helperText != null ? { helperText } : {})}
            {...(helperIcon != null ? { helperIcon } : {})}
            className={className}
            rightIcon={
              <IconChevronDown
                className={cn(
                  'size-3 icon-secondary transition-transform duration-150',
                  isOpen ? 'rotate-180' : '',
                )}
              />
            }
            onFocus={() => {
              if (disabled) return;
              setIsEditing(true);
              setQuery(selectedText);
              setOpen(true);
            }}
            onChange={e => {
              setQuery(e.target.value);
              if (!isOpen) setOpen(true);
            }}
            onBlur={() => {
              // Delay so an option click registers before the menu closes.
              window.setTimeout(() => {
                setIsEditing(false);
                setOpen(false);
              }, 150);
            }}
          />
        ) : (
          <DropdownField
            ref={triggerRef}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-controls={isOpen ? listboxId : undefined}
            disabled={disabled}
            label={label || ''}
            placeholder={placeholder}
            selectedLabel={selectedLabel ?? undefined}
            variant={variant}
            isOpen={isOpen}
            {...(showAlert ? { alert: true } : {})}
            {...(helperText != null ? { helperText } : {})}
            {...(helperIcon != null ? { helperIcon } : {})}
            className={className}
            onClick={() => !disabled && setOpen(!isOpen)}
            onKeyDown={onTriggerKeyDown}
          />
        )}
      </div>

      <Popover
        open={isOpen}
        onOpenChange={setOpen}
        anchorRef={anchorRef as React.RefObject<HTMLElement>}
        matchAnchorWidth={matchTriggerWidth}
        className={cn('')}
      >
        <div onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}>
          {menu}
        </div>
      </Popover>
    </div>
  );
}

export default Dropdown;
